import { Ticket } from '@/types/ticket';

export interface BackendTicket {
  id: string;
  subject: string;
  status: string;
  statusType?: string | null;
  priority: string;
  priorityRaw: number;
  customer: {
    name: string;
    id?: string;
    email?: string;
    phone?: string;
    firstname?: string;
    lastname?: string;
    company?: string;
    title?: string;
    department?: string;
  };
  assignedToUser?: {
    id?: string;
    name?: string;
    email?: string;
  };
  customerId?: string;
  company?: string;
  // Legacy single team fields
  teamId?: string | string[];
  team?: {
    id: string;
    name: string;
  } | Array<{
    id: string;
    name: string;
  }>;
  // Alternative multiple teams structure
  teamIds?: string[];
  teams?: Array<{
    id: string;
    name: string;
  }>;
  assigneeId?: string;
  assigneeStatus?: string;
  assignedTo?: {
    id?: string;
    bot_enabled?: boolean;
  } | null;
  lastMessage: string;
  lastMessageAt: string | null;
  lastMessageBy?: string | null;
  messageCount?: number;
  channel?: string;
  device?: string | null;
  tags?: string[];
  intents?: any;
  language?: string;
  type?: string;
  typeName?: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
  isUnread?: boolean;
  hasNotification?: boolean;
  notificationType?: string | null;
  recipients?: string[];
  summary?: string | null;
  threadId?: string | null;
  externalId?: string | null;
  customFields?: Record<string, any>;
  topicIds?: string[];
  mentionIds?: string[];
  reopenInfo?: any;
  csatDaysRemaining?: number | null;
  permanentCloseDaysRemaining?: number | null;
}

export interface BackendTicketResponse {
  data: BackendTicket[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export const mapBackendTicketToTicket = (backendTicket: BackendTicket): Ticket => {
  return {
    id: backendTicket.id,
    ticket_sno: backendTicket.id,
    sno: parseInt(backendTicket.id) || 0,
    subject: backendTicket.subject,
    description: null,
    customer: backendTicket.customer,
    customerId: backendTicket.customerId,
    customerObj: backendTicket.customer,
    lastMessage: backendTicket.lastMessage,
    lastMessageAt: backendTicket.lastMessageAt,
    lastMessageBy: backendTicket.lastMessageBy,
    messageCount: backendTicket.messageCount,
    status: backendTicket.status,
    statusType: backendTicket.statusType,
    priority: backendTicket.priority,
    priorityRaw: backendTicket.priorityRaw,
    assigneeId: backendTicket.assigneeId,
    assignee: backendTicket.assignedToUser?.name || null,
    assigneeStatus: backendTicket.assigneeStatus,
    assignedTo: backendTicket.assignedTo,
    assignedToUser: backendTicket.assignedToUser,
    // Handle new team structure
    teamId: backendTicket.teamId, // Can be string or array
    team: backendTicket.team, // Can be object or array
    // Alternative structure for backward compatibility
    teamIds: Array.isArray(backendTicket.teamId) ? backendTicket.teamId : undefined,
    teams: Array.isArray(backendTicket.team) ? backendTicket.team : undefined,
    channel: backendTicket.channel,
    device: backendTicket.device,
    tags: backendTicket.tags || [],
    intents: backendTicket.intents,
    language: backendTicket.language,
    type: backendTicket.type,
    typeName: backendTicket.typeName,
    typeId: backendTicket.type,
    createdAt: backendTicket.createdAt,
    updatedAt: backendTicket.updatedAt,
    closedAt: backendTicket.closedAt,
    isUnread: backendTicket.isUnread || false,
    hasNotification: backendTicket.hasNotification || false,
    notificationType: backendTicket.notificationType,
    recipients: backendTicket.recipients || [],
    summary: backendTicket.summary,
    threadId: backendTicket.threadId,
    externalId: backendTicket.externalId,
    customFields: backendTicket.customFields || {},
    topicIds: backendTicket.topicIds || [],
    mentionIds: backendTicket.mentionIds || [],
    reopenInfo: backendTicket.reopenInfo,
    messages: [],
    csatDaysRemaining: backendTicket.csatDaysRemaining ?? null,
    permanentCloseDaysRemaining: backendTicket.permanentCloseDaysRemaining ?? null
  };
};

export const mapBackendResponseToTickets = (response: BackendTicketResponse): Ticket[] => {
  return response.data.map(mapBackendTicketToTicket);
};
